/**
 * The sky's colour as a function of the sun's altitude.
 *
 * Shared rather than duplicated. Both apps paint the twilight band from this
 * ramp, and each renderer stays in its own app — mobile drives
 * `react-native-linear-gradient` props, the webapp composes a CSS
 * `linear-gradient` string, and those genuinely cannot merge. The *palette* is a
 * pure `number -> string` function, so it can, and the two hand-transcribed
 * copies this replaces had to be edited in lockstep (FE#170).
 *
 * A palette normally has no business in core — no hex should cross the
 * shared/platform seam, and colours belong to each app's theme. This one is the
 * exception the rule allows for: it depicts the sky rather than styling a
 * surface, so it is a fact about twilight, not a design token.
 */

const NIGHT = '#0d1330';
const ASTRONOMICAL = '#16225a';
const NAUTICAL = '#24417f';
const CIVIL = '#3d6cae';
/**
 * The colour civil twilight passes through on its way from blue to gold.
 *
 * Civil blue and golden are near-complementary, so the straight line between
 * them runs past the neutral axis: the two-stop ramp bottomed out at #868385
 * near -3.9°, OKLab chroma 0.005 — grey — and painted the hour after sunset in
 * Paris as grey-brown. Changing the interpolation space does not help: a
 * straight OKLab ramp between the same two stops still bottoms out at 0.0105,
 * because any straight line between complements passes near grey whatever the
 * space. A third anchor does. Real civil twilight goes through rose, and
 * routing the ramp through it lifts the worst point to chroma 0.076 — sixteen
 * times more saturated — so the band reads blue → violet → rose → orange
 * rather than blue → mud → orange (FE #119).
 */
const ROSE = '#b0688c';
const GOLDEN = '#f0a44a';
const DAY = '#7cc6ee';
const DAY_HIGH = '#a8dbf5';

const hex = (value: string) => [
  parseInt(value.slice(1, 3), 16),
  parseInt(value.slice(3, 5), 16),
  parseInt(value.slice(5, 7), 16),
];

const mix = (from: string, to: string, ratio: number) => {
  const a = hex(from);
  const b = hex(to);
  const channel = (i: number) =>
    Math.round(a[i] + (b[i] - a[i]) * ratio)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(0)}${channel(1)}${channel(2)}`;
};

/**
 * Sky colour for a solar altitude, in degrees.
 *
 * The thresholds are the twilight definitions: -18° astronomical, -12° nautical,
 * -6° civil, and -0.83° for the horizon itself (the sun's apparent radius plus
 * refraction, which is why sunrise happens with the disc centre still below).
 */
export const skyAt = (altitude: number): string => {
  // A NaN altitude would compose to `#NaNNaNNaN`, which React Native treats as
  // an invalid colour and simply does not paint — the band would vanish with no
  // error, the same failure mode as the `rgb()` tint bug. Unreachable from a
  // real latitude, but the guard costs nothing.
  if (!Number.isFinite(altitude)) {
    return NIGHT;
  }
  if (altitude >= 6) {
    return mix(DAY, DAY_HIGH, Math.min((altitude - 6) / 40, 1));
  }
  if (altitude >= 0.5) {
    return mix(GOLDEN, DAY, (altitude - 0.5) / 5.5);
  }
  if (altitude >= -0.83) {
    return GOLDEN;
  }
  if (altitude >= -6) {
    // Two legs through the rose anchor, hinged at the band's midpoint.
    const ratio = (altitude + 6) / 5.17;
    return ratio < 0.5
      ? mix(CIVIL, ROSE, ratio * 2)
      : mix(ROSE, GOLDEN, (ratio - 0.5) * 2);
  }
  if (altitude >= -12) {
    return mix(NAUTICAL, CIVIL, (altitude + 12) / 6);
  }
  if (altitude >= -18) {
    return mix(ASTRONOMICAL, NAUTICAL, (altitude + 18) / 6);
  }
  return mix(NIGHT, ASTRONOMICAL, Math.max((altitude + 24) / 6, 0));
};
